// src/pages/Subscribe/Subscribe.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, AlertCircle, Lock, ArrowRight } from 'lucide-react';
import apiClient from '../api/client';
import './Subscribe.css';

const PLANS = {
  starter: {
    name: 'Starter',
    limits: '5 users · 2 warehouses · 2 devices',
    price:   { usd: 29,  eur: 27,  tzs: 67000,  ugx: 108000 },
    onetime: { usd: 290, eur: 270, tzs: 670000, ugx: 1080000 },
    features: [
      'Inventory management',
      'Order tracking',
      'Basic reports',
      'Offline mode',
      'Email support',
      'Mobile access',
    ],
  },
  business: {
    name: 'Business',
    limits: '15 users · 5 warehouses · 5 devices',
    price:   { usd: 79,   eur: 75,  tzs: 182000,  ugx: 295000 },
    onetime: { usd: 790,  eur: 750, tzs: 1820000, ugx: 2950000 },
    popular: true,
    features: [
      'Everything in Starter',
      'Advanced analytics',
      'Stock predictions',
      'Custom reports',
      'Priority support',
    ],
  },
  professional: {
    name: 'Professional',
    limits: 'Unlimited users & warehouses · 10 devices',
    price:   { usd: 199,  eur: 189,  tzs: 460000,  ugx: 745000 },
    onetime: { usd: 1990, eur: 1890, tzs: 4600000, ugx: 7450000 },
    features: [
      'Everything in Business',
      'AI predictions',
      'API access',
      'White-label option',
      'Dedicated support',
    ],
  },
};

const CURRENCIES = [
  { key: 'eur', label: 'EUR €', flag: '🇪🇺' },
  { key: 'usd', label: 'USD $', flag: '🇺🇸' },
  { key: 'tzs', label: 'TZS',   flag: '🇹🇿' },
  { key: 'ugx', label: 'UGX',   flag: '🇺🇬' },
];

function fmt(price, currency) {
  const sym = { usd: '$', eur: '€', tzs: 'TSh ', ugx: 'UGX ' };
  if ((currency === 'tzs' || currency === 'ugx') && price >= 1000)
    return `${sym[currency]}${(price / 1000).toFixed(0)}K`;
  return `${sym[currency]}${price.toLocaleString()}`;
}

function Subscribe() {
  const navigate = useNavigate();
  const [loading, setLoading]               = useState(false);
  const [selectedPlan, setSelectedPlan]     = useState('business');
  const [selectedCurrency, setSelectedCurrency] = useState('eur');
  const [billing, setBilling]               = useState('monthly');
  const [lockedCurrency, setLockedCurrency] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [hasValidSubscription, setHasValidSubscription] = useState(false);
  const [error, setError]                   = useState(null);
  const [fetchingStatus, setFetchingStatus] = useState(true);

  useEffect(() => { fetchSubscriptionStatus(); }, []);

  const fetchSubscriptionStatus = async () => {
    setFetchingStatus(true);
    setError(null);
    try {
      const response = await apiClient.get('/subscriptions/status/');
      setSubscriptionInfo(response.data);
      const isValid = response.data.is_valid === true && response.data.is_active === true;
      setHasValidSubscription(isValid);
      if (response.data.status === 'paid' || response.data.status === 'past_due') {
        const c = response.data.currency?.toLowerCase();
        if (c) { setLockedCurrency(c); setSelectedCurrency(c); }
      }
    } catch (err) {
      const errType = err.response?.data?.error;
      setError(
        errType === 'NO_SUBSCRIPTION' ? 'No active subscription. Choose a plan below.' :
        errType === 'NO_ORGANIZATION' ? 'Account setup incomplete. Contact support.' :
        err.response?.status === 404  ? 'Choose a plan below to get started.' :
        'Failed to load subscription status. Please refresh.'
      );
      setHasValidSubscription(false);
    } finally {
      setFetchingStatus(false);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/subscriptions/create-checkout/', {
        plan: selectedPlan,
        currency: selectedCurrency,
        billing_type: billing,
        success_url: `${window.location.origin}/dashboard?payment=success`,
        cancel_url:  `${window.location.origin}/subscribe?payment=cancelled`,
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        setError('Failed to create checkout. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Checkout failed. Please try again.');
      setLoading(false);
    }
  };

  const plan = PLANS[selectedPlan];
  const price = billing === 'monthly' ? plan.price[selectedCurrency] : plan.onetime[selectedCurrency];

  if (fetchingStatus) return (
    <div className="subscribe-page">
      <div className="loading-state">
        <div className="spinner"/>
        <p>Loading subscription status…</p>
      </div>
    </div>
  );

  return (
    <div className="subscribe-page">
      <div className="subscribe-container">

        {/* Header */}
        <div className="subscribe-header">
          <h1>Simple, <em>transparent</em> pricing.</h1>

          {/* Status banner */}
          {subscriptionInfo && (
            <div className={`trial-info ${subscriptionInfo.days_remaining <= 3 ? 'urgent' : ''} ${subscriptionInfo.status === 'paid' ? 'active-sub' : ''}`}>
              {subscriptionInfo.status === 'trial' ? (
                subscriptionInfo.days_remaining > 0
                  ? <><strong>🎉 {subscriptionInfo.days_remaining} days</strong> of free trial remaining · ends {new Date(subscriptionInfo.end_date).toLocaleDateString()}</>
                  : <>⏰ Your free trial has ended — subscribe to continue</>
              ) : subscriptionInfo.status === 'paid' ? (
                <>✅ <strong>{subscriptionInfo.plan_display}</strong> active · renews {new Date(subscriptionInfo.end_date).toLocaleDateString()}</>
              ) : (
                <>❌ Subscription expired — renew to restore access</>
              )}
            </div>
          )}

          {error && (
            <div className="error-banner">
              <AlertCircle size={16}/> {error}
            </div>
          )}

          <p className="subscribe-subtitle">
            {hasValidSubscription
              ? 'Upgrade or change your plan anytime'
              : 'Full access during trial — choose a plan when you\'re ready'}
          </p>
        </div>

        {/* Controls */}
        <div className="subscribe-controls">
          {/* Currency */}
          <div className="currency-selector">
            {lockedCurrency && (
              <div className="currency-lock-notice">
                <Lock size={13}/> Currency locked to your existing subscription
              </div>
            )}
            {CURRENCIES.map(c => (
              <button
                key={c.key}
                className={`currency-btn ${selectedCurrency === c.key ? 'active' : ''} ${lockedCurrency && c.key !== lockedCurrency ? 'locked' : ''}`}
                onClick={() => !lockedCurrency || c.key === lockedCurrency ? setSelectedCurrency(c.key) : null}
                disabled={lockedCurrency && c.key !== lockedCurrency}
              >
                {c.flag} {c.label}
                {lockedCurrency && c.key !== lockedCurrency && <Lock size={11}/>}
              </button>
            ))}
          </div>

          {/* Billing toggle */}
          <div className="billing-toggle">
            <button className={`bill-btn ${billing === 'monthly' ? 'active' : ''}`} onClick={() => setBilling('monthly')}>Monthly</button>
            <button className={`bill-btn ${billing === 'onetime' ? 'active' : ''}`} onClick={() => setBilling('onetime')}>One-time</button>
          </div>
        </div>

        {/* Pricing cards */}
        <div className="pricing-grid">
          {Object.entries(PLANS).map(([key, p]) => (
            <div
              key={key}
              className={`pricing-card ${p.popular ? 'popular' : ''} ${selectedPlan === key ? 'selected' : ''}`}
              onClick={() => setSelectedPlan(key)}
            >
              {p.popular && <div className="popular-badge">Most Popular</div>}
              <div className="plan-name">{p.name}</div>
              <div className="plan-limits">{p.limits}</div>
              <div className="plan-price">
                <span className="price-amount">{fmt(billing === 'monthly' ? p.price[selectedCurrency] : p.onetime[selectedCurrency], selectedCurrency)}</span>
                <span className="price-period">{billing === 'monthly' ? '/mo' : ' once'}</span>
              </div>
              {billing === 'monthly' && (
                <p className="price-alt">or {fmt(p.onetime[selectedCurrency], selectedCurrency)} one-time</p>
              )}
              <ul className="plan-features">
                {p.features.map((f, i) => (
                  <li key={i}><Check size={13}/> {f}</li>
                ))}
              </ul>
              {selectedPlan === key && (
                <div className="selected-indicator"><Check size={13}/> Selected</div>
              )}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="subscribe-actions">
          <button className="btn-subscribe" onClick={handleSubscribe} disabled={loading}>
            {loading ? 'Processing…' : `Subscribe to ${plan.name}`} <ArrowRight size={16}/>
          </button>
          <button className="btn-cancel" onClick={() => navigate(hasValidSubscription ? '/dashboard' : '/login')}>
            {hasValidSubscription ? 'Back to Dashboard' : 'Back to Login'}
          </button>
        </div>

        {/* Footer */}
        <div className="subscribe-footer">
          <p>🔒 Secure payment via Stripe · Cancel anytime · Full access during trial</p>
          {lockedCurrency && (
            <p><span className="currency-notice">Currency cannot be changed after your first payment. Contact support if needed.</span></p>
          )}
        </div>

      </div>
    </div>
  );
}

export default Subscribe;